import type {
  AttachmentId,
  CategoryId,
  DateString,
  EditActionDto,
  ExpenseTypeId,
  Money,
  GroupId,
  SourceId,
} from "../api/types";
import type { StoredTransaction } from "./derivedAccount";

/// A typed change-set for a single transaction field. Entity-valued fields
/// carry ids — the picker hands the id straight through to the action DTO
/// without a name lookup, dodging the stale-name-after-inline-create bug.
export type FieldChange =
  | { field: "datePaid"; value: DateString }
  | { field: "source"; value: SourceId }
  | { field: "merchant"; value: string }
  | { field: "description"; value: string }
  | { field: "group"; value: GroupId }
  | { field: "foreignAmount"; value: Money | null }
  | { field: "amount"; value: Money }
  | { field: "expenseType"; value: ExpenseTypeId }
  | { field: "categories"; value: CategoryId[] }
  | { field: "receiptId"; value: AttachmentId | null }
  | { field: "isIgnored"; value: boolean };

export const fieldChangeToActions = (
  change: FieldChange,
  effective: StoredTransaction,
  /// Resolver from a category name to its id — needed to translate the
  /// transaction's current category names (the only side that's still
  /// name-typed in `TransactionListDto`) into ids when computing the diff.
  /// Categories whose names don't resolve are skipped from removal.
  resolveCategoryId?: (name: string) => CategoryId | undefined,
): EditActionDto[] => {
  switch (change.field) {
    case "categories": {
      const oldNames = effective.categories ?? [];
      const oldIds = resolveCategoryId
        ? oldNames
            .map((n) => resolveCategoryId(n))
            .filter((id): id is CategoryId => id !== undefined)
        : [];
      const newIds = change.value;
      // One action per direction, not one per category: each carries a list,
      // so a multi-select that adds three leaves one row to review and one
      // line in the edit history.
      const added = newIds.filter((id) => !oldIds.includes(id));
      const removed = oldIds.filter((id) => !newIds.includes(id));
      return [
        ...(added.length
          ? [{ field: "addCategory", value: added } as const]
          : []),
        ...(removed.length
          ? [{ field: "removeCategory", value: removed } as const]
          : []),
      ];
    }
    case "receiptId":
      // Confirming the picker with the receipt unchanged (including "nothing
      // selected" on a transaction that already has none) is not an edit.
      return (effective.receiptId ?? null) === change.value
        ? []
        : [{ field: "receipt", value: change.value }];
    case "isIgnored":
      return [{ field: "ignore", value: change.value }];
    case "datePaid":
      return [{ field: "datePaid", value: change.value }];
    case "source":
      return [{ field: "source", value: change.value }];
    case "merchant":
      return [{ field: "merchant", value: change.value }];
    case "description":
      return [{ field: "description", value: change.value }];
    case "group":
      return [{ field: "group", value: change.value }];
    case "foreignAmount":
      return [{ field: "foreignAmount", value: change.value }];
    case "amount":
      return [{ field: "amount", value: change.value }];
    case "expenseType":
      return [{ field: "expenseType", value: change.value }];
  }
};

/// Actions that mean "set this field to this value". A later one fully
/// supersedes an earlier one, so only the newest has to be kept. The rest —
/// `addCategory`/`removeCategory`, `addAttachment`/`removeAttachment`,
/// `split`, `applyAutoTx`, `conditional`, `prompt` — accumulate or depend on
/// where they sit in the sequence, so they are always preserved.
const LAST_WRITE_WINS_FIELDS = new Set<EditActionDto["field"]>([
  "datePaid",
  "source",
  "merchant",
  "description",
  "group",
  "foreignAmount",
  "amount",
  "expenseType",
  "receipt",
  "invoice",
  "ignore",
]);

/// Actions the server resolves against the transaction state as it stands when
/// they run. A field write positioned before one of these may be what it
/// branches on, so it can't be dropped even if a later write supersedes it.
const STATE_DEPENDENT_FIELDS = new Set<EditActionDto["field"]>([
  "applyAutoTx",
  "conditional",
]);

/// Appends `incoming` to `previous`, collapsing superseded writes so a field
/// edited N times contributes one action instead of N. Typing into an inline
/// field is the motivating case: without this, every keystroke leaves its own
/// pending row and its own entry in the saved edit history.
///
/// Only writes that sit *after* the last state-dependent action are collapsed
/// — see `STATE_DEPENDENT_FIELDS`.
export const coalesceActions = (
  previous: EditActionDto[],
  incoming: EditActionDto[],
): EditActionDto[] => {
  let combined = previous;

  for (const action of incoming) {
    if (!LAST_WRITE_WINS_FIELDS.has(action.field)) {
      combined = [...combined, action];
      continue;
    }

    let boundary = -1;
    for (let i = combined.length - 1; i >= 0; i--) {
      if (STATE_DEPENDENT_FIELDS.has(combined[i].field)) {
        boundary = i;
        break;
      }
    }

    combined = [
      ...combined.slice(0, boundary + 1),
      ...combined.slice(boundary + 1).filter((a) => a.field !== action.field),
      action,
    ];
  }

  return combined;
};
